import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  DialogContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Checkbox as MuiCheckbox,
  Icon as MuiIcon,
  TextField,
} from '@mui/material'
import {
  DigitalTwinSettingCreationRequest,
  setSettingRequestMinutes,
  splitTimeSeriesSettingOnNullValues,
  TimeSeriesSettingCreationRequest,
} from 'api/digitalTwin/digitalTwin.api'
import {
  useEventCreateMutation,
  useEventDeleteMutation,
  useEventUpdateMutation,
  useGetEventConfig,
} from 'api/events/events.api'
import classNames from 'classnames'
import Delta from 'quill-delta'
import React, { ReactElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactQuill from 'react-quill-new'
import alertStore from 'store/alert/alert'
import { ALERT_KEYS } from 'store/alert/alert.constants'
import { colors } from 'styles/variables'
import { Button } from 'ui/atoms'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { Dialog } from 'ui/components/Dialog/Dialog'
import Dropdown from 'ui/components/Dropdown/Dropdown'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SearchableDropdown from 'ui/components/SearchableDropdown/SearchableDropdown'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import CopyDialogTitle from 'ui/molecules/CopyDialogTitle/CopyDialogTitle'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import Datetime from 'utils/datetime/datetime'
import EventPropertiesDropdownsSettingInstructions from '../EventPropertiesDropdowns/EventPropertiesDropdownsSettingInstructions'
import EventSettingsInstructionsOverviewTable from '../EventSettingsOverviewTable/EventSettingsInstructionsOverviewTable'
import EventStatus from '../EventStatus/EventStatus'
import {
  checkIfEditing,
  createdAndUpdatedInfoEventPost,
  EMPTY_LINKED_OBJECT_ATTRIBUTE,
  EVENT_TYPES,
  EventChangeRequest,
  EventPost,
  EventStatus as EventStatusType,
  generateUniqueId,
  getEventPostForSchedulabilityChange,
  getNumberOfSettingInstructions,
  hasDuplicateLinkedObjects,
  hasInitialEventPostChanges,
  hasOversizedEmbeddedImage,
  hasSettingsOutsideTimePeriod,
  type InputFieldDeltas,
  isEquivalentEditorContent,
  MAX_IMAGE_SIZE_MB,
  MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH,
  normalizeLinkedObjectAttribute,
  noSettingWritePermissionSettingInstructions,
  noWritePermission,
  PermittedActionsEventPost,
  reactQuillModules,
  SCHEDULABILITIES,
  SettingOrTimeSeriesInstructions,
  TimeSeriesSettingInstruction,
  useInputFieldEditorContent,
} from '../events.helper'
import styles from './EventPostModal.module.less'
import { EventPostModalDialogs } from './EventPostModalDialogs'
import {
  type EventPostModalUiAction,
  type EventPostModalWrite,
  eventPostModalUiReducer,
  initialEventPostModalUiState,
} from './eventPostModalUi'
import { type EventPostModalEditState, useAppliedEventPost } from './useAppliedEventPost'

type EventPostModalProps = {
  closeModal: () => void
  newEvent?: boolean
  selectedEventPostId?: number | null
}

type EventPostModalContentProps = EventPostModalProps & {
  appliedEventPost?: EventPost | null
  isFetching: boolean
  hasWithheldServerUpdate: boolean
  onEditStateChange: (editState: EventPostModalEditState) => void
  onEventCreated: (createdEventPost: EventPost) => void
}

type EventPostModalActionButtonsProps = {
  statusDraft: boolean
  statusSubmitted: boolean
  statusActive: boolean
  statusInactive: boolean
  isEditing: boolean
  isFetching: boolean
  isWritingEvent: boolean
  disabledSaveDraft: boolean
  disabledSubmit: boolean
  hasChangeRequest: boolean
  CHANGE_REQUEST_PERMISSION: boolean
  eventPost: EventPost
  permittedTo: (action: PermittedActionsEventPost) => boolean
  requestWrite: (write: EventPostModalWrite) => void
  dispatchUi: React.Dispatch<EventPostModalUiAction>
}

type EventPostModalAffectedComponentsSectionProps = {
  isSettingsOutsideTimePeriod: boolean
  settingInstructions: SettingOrTimeSeriesInstructions[]
  eventPost: EventPost
  linkedObjectFromInstructions:
    | NonNullable<NonNullable<EventPost['setting_instructions']>['linked_objects']>
    | undefined
  disabledSameLinkedObjects: boolean
  permittedTo: (action: PermittedActionsEventPost) => boolean
  dispatchUi: React.Dispatch<EventPostModalUiAction>
  toggleSettingOverview: (index: number) => void
  openSettingsOverview: { [index: number]: boolean }
  updateSettingInstructions: (data: DigitalTwinSettingCreationRequest & { delete?: boolean; id?: number }) => void
  setEventPost: React.Dispatch<React.SetStateAction<EventPost>>
  addEventPropertyField: () => void
}

type EventPostModalViewState = {
  openSettingsOverview: { [index: number]: boolean }
  editorErrorMessage: string
  hasInvalidDates: boolean
  hasInvalidDateLimit: boolean
}

export default function EventPostModal({
  closeModal,
  newEvent,
  selectedEventPostId,
}: EventPostModalProps): ReactElement {
  return useEventPostModalView({ closeModal, newEvent, selectedEventPostId })
}

function useEventPostModalView({ closeModal, newEvent, selectedEventPostId }: EventPostModalProps): ReactElement {
  const {
    appliedEventPost,
    isFetching,
    hasWithheldServerUpdate,
    setModalEditState,
    adoptCreatedEventPost,
    appliedRevisionKey,
  } = useAppliedEventPost(selectedEventPostId)

  if (selectedEventPostId && !appliedEventPost && !newEvent) {
    return <></>
  }

  return (
    <EventPostModalContent
      key={appliedRevisionKey}
      closeModal={closeModal}
      newEvent={newEvent}
      selectedEventPostId={selectedEventPostId}
      appliedEventPost={appliedEventPost}
      isFetching={isFetching}
      hasWithheldServerUpdate={hasWithheldServerUpdate}
      onEditStateChange={setModalEditState}
      onEventCreated={adoptCreatedEventPost}
    />
  )
}

function EventPostModalActionButtons({
  statusDraft,
  statusSubmitted,
  statusActive,
  statusInactive,
  isEditing,
  isFetching,
  isWritingEvent,
  disabledSaveDraft,
  disabledSubmit,
  hasChangeRequest,
  CHANGE_REQUEST_PERMISSION,
  eventPost,
  permittedTo,
  requestWrite,
  dispatchUi,
}: EventPostModalActionButtonsProps): ReactElement {
  const { t } = useTranslation()

  return (
    <div className={styles.EventPostModal_Buttons}>
      <div className={styles.EventPostModal_Buttons_Left}>
        {statusDraft ? (
          <Tooltip
            title={!permittedTo('delete_event') ? t('You do not have permission to throw away this event') : ''}
            arrow
          >
            <div>
              <Button
                disabled={!permittedTo('delete_event') || isWritingEvent}
                onClick={() => dispatchUi({ type: 'set', key: 'confirmOnThrowAwayModalOpen', value: true })}
                className={permittedTo('delete_event') ? styles.EventPostModal_Buttons_Left_ThrowAwayButton : undefined}
              >
                {t('Throw away')}
              </Button>
            </div>
          </Tooltip>
        ) : (
          <Tooltip
            title={!permittedTo('delete_event') ? t('You do not have permission to delete this event') : ''}
            arrow
          >
            <div>
              <Button
                danger
                disabled={!permittedTo('delete_event') || isWritingEvent}
                onClick={() => dispatchUi({ type: 'set', key: 'confirmDeleteModalOpen', value: true })}
              >
                {t('Delete')}
              </Button>
            </div>
          </Tooltip>
        )}
      </div>
      <div className={styles.EventPostModal_Buttons_Right}>
        {isFetching && (
          <span className="flex items-center">
            <CircularProgress size={24} />
          </span>
        )}
        {statusDraft && (
          <>
            <Tooltip
              title={
                !permittedTo('update') || disabledSaveDraft ? t('You do not have permission to update this event') : ''
              }
              arrow
            >
              <div>
                <Button
                  className={
                    !permittedTo('update') || disabledSaveDraft
                      ? styles.EventPostModal_Buttons__DisabledButton
                      : styles.EventPostModal_Buttons__DraftButton
                  }
                  disabled={!permittedTo('update') || disabledSaveDraft || isWritingEvent}
                  onClick={() => requestWrite('draft')}
                >
                  {t('Save draft')}
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title={
                !permittedTo('send') && !permittedTo('unsend')
                  ? t('You do not have permission to submit this event')
                  : disabledSubmit
                    ? t('Please fill out all required * fields to continue')
                    : ''
              }
              arrow
            >
              <div>
                <Button
                  primary
                  disabled={disabledSubmit || isWritingEvent || (!permittedTo('send') && !permittedTo('unsend'))}
                  onClick={() => requestWrite('submit')}
                >
                  {t('Submit')}
                </Button>
              </div>
            </Tooltip>
          </>
        )}
        {isEditing && !statusDraft ? (
          <>
            {CHANGE_REQUEST_PERMISSION && hasChangeRequest && (
              <Button primary onClick={() => dispatchUi({ type: 'set', key: 'openEventEditRequest', value: true })}>
                {t('View change request')}
              </Button>
            )}
            <Tooltip
              title={
                !permittedTo('update')
                  ? t('You do not have permission to save this event')
                  : disabledSubmit
                    ? t('Please fill out all required * fields to continue')
                    : ''
              }
              arrow
            >
              <div>
                <Button
                  primary
                  disabled={disabledSubmit || isWritingEvent || !permittedTo('update')}
                  onClick={() => {
                    if (eventPost.pending_change_request_id && CHANGE_REQUEST_PERMISSION) {
                      dispatchUi({ type: 'set', key: 'confirmSaveEventWithChangeRequest', value: true })
                    } else {
                      requestWrite('save')
                    }
                  }}
                >
                  {t('Save')}
                </Button>
              </div>
            </Tooltip>
          </>
        ) : (
          <>
            {statusSubmitted && (
              <>
                <Tooltip
                  title={!permittedTo('update') ? t('You do not have permission to update this event') : ''}
                  arrow
                >
                  <div>
                    <Button
                      className={
                        !permittedTo('update')
                          ? styles.EventPostModal_Buttons__DisabledButton
                          : styles.EventPostModal_Buttons__DraftButton
                      }
                      disabled={!permittedTo('update') || isWritingEvent}
                      onClick={() => requestWrite('draft')}
                    >
                      {t('Convert to draft')}
                    </Button>
                  </div>
                </Tooltip>
                <Tooltip
                  title={
                    !permittedTo('activate') && !permittedTo('reactivate')
                      ? t('You do not have permission to activate this event')
                      : disabledSubmit
                        ? t('Please fill out all required * fields to continue')
                        : ''
                  }
                  arrow
                >
                  <div>
                    <Button
                      disabled={
                        disabledSubmit || isWritingEvent || (!permittedTo('activate') && !permittedTo('reactivate'))
                      }
                      className={
                        disabledSubmit || (!permittedTo('activate') && !permittedTo('reactivate'))
                          ? styles.EventPostModal_Buttons__DisabledButton
                          : styles.EventPostModal_Buttons__ActiveButton
                      }
                      onClick={() => requestWrite('activate')}
                    >
                      {t('Activate')}
                    </Button>
                  </div>
                </Tooltip>
              </>
            )}
            {statusActive && (
              <div className="flex gap-1">
                {CHANGE_REQUEST_PERMISSION &&
                  (hasChangeRequest ? (
                    <Button
                      primary
                      onClick={() => dispatchUi({ type: 'set', key: 'openEventEditRequest', value: true })}
                    >
                      {t('View change request')}
                    </Button>
                  ) : permittedTo('create_change_request') ? (
                    <Button
                      secondary
                      onClick={() => dispatchUi({ type: 'set', key: 'openEventEditRequest', value: true })}
                    >
                      {t('Submit a change request')}
                    </Button>
                  ) : null)}
                <Tooltip
                  title={
                    !permittedTo('deactivate')
                      ? t('You do not have permission to inactivate this event')
                      : disabledSubmit
                        ? t('Please fill out all required * fields to continue')
                        : ''
                  }
                  arrow
                >
                  <div>
                    <Button
                      disabled={disabledSubmit || isWritingEvent || !permittedTo('deactivate')}
                      className={
                        disabledSubmit || !permittedTo('deactivate')
                          ? styles.EventPostModal_Buttons__DisabledButton
                          : styles.EventPostModal_Buttons__InactiveButton
                      }
                      onClick={() => requestWrite('inactivate')}
                    >
                      {t('Inactivate')}
                    </Button>
                  </div>
                </Tooltip>
              </div>
            )}
            {statusInactive && (
              <div className="flex gap-1">
                {CHANGE_REQUEST_PERMISSION && (hasChangeRequest || !noWritePermission(eventPost)) && (
                  <Button
                    primary={hasChangeRequest}
                    secondary={!hasChangeRequest}
                    onClick={() => dispatchUi({ type: 'set', key: 'openEventEditRequest', value: true })}
                  >
                    {hasChangeRequest ? t('View change request') : t('Submit a change request')}
                  </Button>
                )}
                <Tooltip
                  title={
                    !permittedTo('activate') && !permittedTo('reactivate')
                      ? t('You do not have permission to activate this event')
                      : disabledSubmit
                        ? t('Please fill out all required * fields to continue')
                        : ''
                  }
                  arrow
                >
                  <div>
                    <Button
                      disabled={
                        disabledSubmit || isWritingEvent || (!permittedTo('activate') && !permittedTo('reactivate'))
                      }
                      className={
                        disabledSubmit || (!permittedTo('activate') && !permittedTo('reactivate'))
                          ? styles.EventPostModal_Buttons__DisabledButton
                          : styles.EventPostModal_Buttons__ActiveButton
                      }
                      onClick={() => requestWrite('activate')}
                    >
                      {t('Activate')}
                    </Button>
                  </div>
                </Tooltip>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function EventPostModalAffectedComponentsSection({
  isSettingsOutsideTimePeriod,
  settingInstructions,
  eventPost,
  linkedObjectFromInstructions,
  disabledSameLinkedObjects,
  permittedTo,
  dispatchUi,
  toggleSettingOverview,
  openSettingsOverview,
  updateSettingInstructions,
  setEventPost,
  addEventPropertyField,
}: EventPostModalAffectedComponentsSectionProps): ReactElement {
  const { t } = useTranslation()

  return (
    <>
      <Divider className={styles.EventPostModal_Divider} />
      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={true}
        sx={{
          '&:before': {
            // Disable separator above accordion in collapsed mode
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<MuiIcon fontSize="small" className={`fas fa-chevron-down`} />}
          sx={{
            '&.MuiAccordionSummary-root': {
              padding: 0,
            },
          }}
        >
          <span>{t('Affected components')}</span>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            '&.MuiAccordionDetails-root': {
              padding: '0',
            },
          }}
        >
          {isSettingsOutsideTimePeriod && (
            <div className={styles.EventPostModal_FormRow} style={{ marginBottom: '15px' }}>
              <InfoBanner
                text={t(`There are settings in this event outside the event time period`)}
                style={'warning'}
              />
            </div>
          )}
          {!linkedObjectFromInstructions ? (
            <></>
          ) : (
            linkedObjectFromInstructions.map((linkedObject, index) => {
              return (
                <div
                  key={`${linkedObject.level}-${linkedObject.name}-${normalizeLinkedObjectAttribute(linkedObject.attribute)}`}
                >
                  <div className={styles.EventPostModal_AlignCenter}>
                    <Tooltip
                      title={
                        getNumberOfSettingInstructions(settingInstructions, eventPost, index) > 0
                          ? t(
                              `Affected component can't be changed when settings are connected. Remove settings to change these values or add a new affected component.`
                            )
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <div className={styles.EventPostModal_FormRow}>
                        <EventPropertiesDropdownsSettingInstructions
                          eventPost={eventPost}
                          setEventPost={(event: EventPost | EventChangeRequest) => setEventPost(event as EventPost)}
                          linkedObject={{
                            level: linkedObject.level,
                            name: linkedObject.name,
                            attribute: linkedObject.attribute,
                          }}
                          error={disabledSameLinkedObjects}
                          index={index}
                          disabled={
                            getNumberOfSettingInstructions(settingInstructions, eventPost, index) > 0 ||
                            !permittedTo('update')
                          }
                        />
                      </div>
                    </Tooltip>

                    {linkedObjectFromInstructions && (
                      <>
                        <div className={styles.EventPostModal_FormRow_ActionButtons}>
                          <Icon
                            icon="fas fa-times-circle"
                            color="red"
                            large
                            disabled={
                              !permittedTo('update') || noSettingWritePermissionSettingInstructions(eventPost, index)
                            }
                            onClick={() => {
                              dispatchUi({
                                type: 'set',
                                key: 'confirmOnRemoveAffectedComponentModalOpen',
                                value: true,
                              })
                              dispatchUi({ type: 'setSelectedAffectedComponent', value: index })
                            }}
                            tooltip={{
                              title:
                                !permittedTo('update') || noSettingWritePermissionSettingInstructions(eventPost, index)
                                  ? t('You do not have permission to remove an affected component')
                                  : t('Remove affected component'),
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                          <Icon
                            icon="fas fa-cog"
                            color={colors.grey6}
                            large
                            data-testid="add-setting-gear" // e2e: open setting overview for affected component — do not remove
                            disabledTooltip={t('Select a property to add settings')}
                            onClick={() => toggleSettingOverview(index)}
                            tooltip={{
                              title: !permittedTo('update') ? t('View settings') : t('View/add settings'),
                              placement: 'top',
                              arrow: true,
                            }}
                          />
                          {getNumberOfSettingInstructions(settingInstructions, eventPost, index) > 0 && (
                            <Tooltip
                              title={`${getNumberOfSettingInstructions(settingInstructions, eventPost, index)} ${t('Settings')}`}
                              arrow
                              placement="top"
                            >
                              <div className={styles.EventPostModal_YellowCircle} />
                            </Tooltip>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {(openSettingsOverview[index] ?? true) && (
                    <EventSettingsInstructionsOverviewTable
                      index={index}
                      eventPost={eventPost}
                      level={linkedObjectFromInstructions?.[index]?.level || ''}
                      name={linkedObjectFromInstructions?.[index]?.name || ''}
                      attribute={linkedObjectFromInstructions?.[index]?.attribute || ''}
                      settings={settingInstructions.filter((setting) => {
                        return (
                          setting.name === linkedObjectFromInstructions?.[index]?.name &&
                          setting.attribute === linkedObjectFromInstructions?.[index]?.attribute &&
                          setting.level === linkedObjectFromInstructions?.[index]?.level
                        )
                      })}
                      setSettingInstructionsData={updateSettingInstructions}
                    />
                  )}
                </div>
              )
            })
          )}

          {!permittedTo('update') ? (
            <></>
          ) : (
            <div className={styles.EventPostModal_AddProperty}>
              <Button
                link
                icon="fal fa-plus"
                onClick={addEventPropertyField}
                data-testid="add-affected-component-button" // e2e: add affected component — do not remove
              >
                {t('Add')}
              </Button>
              {disabledSameLinkedObjects && (
                <InfoBanner text={t(`You can't have two of the same affected components`)} style={'danger'} />
              )}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  )
}

function EventPostModalContent({
  closeModal,
  newEvent,
  selectedEventPostId,
  appliedEventPost,
  isFetching,
  hasWithheldServerUpdate,
  onEditStateChange,
  onEventCreated,
}: EventPostModalContentProps): ReactElement {
  return useEventPostModalContentView({
    closeModal,
    newEvent,
    selectedEventPostId,
    appliedEventPost,
    isFetching,
    hasWithheldServerUpdate,
    onEditStateChange,
    onEventCreated,
  })
}

function useEventPostModalContentView({
  closeModal,
  newEvent,
  selectedEventPostId,
  appliedEventPost,
  isFetching,
  hasWithheldServerUpdate,
  onEditStateChange,
  onEventCreated,
}: EventPostModalContentProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem, systemId, hasAccess } = useAuth()
  const activeDigitalTwinId = activeSystem?.primary_digital_twin?.id

  const CHANGE_REQUEST_PERMISSION = hasAccess({
    module: 'events',
    submodule: 'change_requests',
    permission: 'view_change_requests',
  })

  const initialEventModalState = useMemo((): EventPost => {
    return {
      digital_twin_id: activeDigitalTwinId,
      system_id: systemId,
      start_time: Datetime.getISONow(),
      end_time: Datetime.getEndOfDay(Datetime.getISONow()),
      require_umm: false,
      status: 'Draft',
      input_field: {},
      setting_instructions: { linked_objects: [] },
      permitted_actions: ['send', 'update'],
    }
  }, [activeDigitalTwinId, systemId])

  const [eventPost, setEventPost] = useState<EventPost>(appliedEventPost ?? initialEventModalState)
  const [uiState, dispatchUi] = useReducer(eventPostModalUiReducer, initialEventPostModalUiState)
  const [lastSavedValues, setLastSavedValuesState] = useState<EventPost>(appliedEventPost ?? initialEventModalState)
  const [inputFieldDeltaState, setInputFieldDeltaState] = useState<{
    saved: Delta | null
    current: Delta | null
  }>({
    saved: null,
    current: null,
  })

  const setLastSavedValues = useCallback((values: EventPost) => {
    setLastSavedValuesState(values)
    setInputFieldDeltaState({ saved: null, current: null })
  }, [])
  const [viewState, setViewState] = useState<EventPostModalViewState>({
    openSettingsOverview: {},
    editorErrorMessage: '',
    hasInvalidDates: false,
    hasInvalidDateLimit: false,
  })

  const linkedObjectFromInstructions = eventPost?.setting_instructions?.linked_objects

  const settingInstructions: SettingOrTimeSeriesInstructions[] = useMemo(() => {
    const instructions: SettingOrTimeSeriesInstructions[] = []
    linkedObjectFromInstructions?.forEach((linkedObject) => {
      if (linkedObject.time_series_setting_instructions) {
        instructions.push(...linkedObject.time_series_setting_instructions)
      }
      if (linkedObject.setting_instructions) {
        instructions.push(...linkedObject.setting_instructions)
      }
    })

    return instructions
  }, [linkedObjectFromInstructions])

  const { mutateAsync: updateEventPost, isPending: isUpdatingEvent } = useEventUpdateMutation()
  const { mutateAsync: deleteEventPost, isPending: isDeletingEvent } = useEventDeleteMutation()
  const { mutateAsync: createEventPost, isPending: isCreatingEvent } = useEventCreateMutation()

  // An unsaved event has no id until its POST returns, so a second action taken before then also
  // reads "unsaved" and creates a duplicate event (#4679).
  const isSavingEvent = isCreatingEvent || isUpdatingEvent
  // A DELETE holds the same latch, so a footer write offered during one would be dropped. It stays
  // out of `isSavingEvent` because that also decides which server revisions are withheld.
  const isWritingEvent = isSavingEvent || isDeletingEvent
  // The state flag is per-render, so it cannot stop two activations delivered before the pending
  // state is published. The ref is written synchronously and is what holds the door.
  const isSavingRef = useRef(false)

  const eventConfig = useGetEventConfig(systemId).data
  const eventCategories = eventConfig?.categories
  const quillRef = useRef<ReactQuill | null>(null)
  const lastSeededSavedValues = useRef<EventPost | null>(null)
  const isSettingsOutsideTimePeriod = hasSettingsOutsideTimePeriod(settingInstructions, eventPost)
  const unsavedEvent = eventPost.id === undefined || eventPost.id === null
  const schedulabilityEffectLength = eventPost?.schedulability_effect?.length ?? 0

  const currentStatus = eventPost.status ?? ('Draft' as EventStatusType)
  const statusDraft = currentStatus === 'Draft'
  const statusSubmitted = currentStatus === 'Sent'
  const statusActive = currentStatus === 'Active'
  const statusInactive = currentStatus === 'Inactive'

  const inputFieldDeltas: InputFieldDeltas | undefined =
    inputFieldDeltaState.saved == null
      ? undefined
      : {
          saved: inputFieldDeltaState.saved,
          current: inputFieldDeltaState.current,
        }
  const isEditing = checkIfEditing(eventPost, lastSavedValues, eventConfig, inputFieldDeltas)

  const editState: EventPostModalEditState = isSavingEvent ? 'saving' : isEditing ? 'editing' : 'clean'
  useEffect(() => {
    onEditStateChange(editState)
  }, [editState, onEditStateChange])

  const hasChangeRequest = (eventPost.change_requests?.length ?? 0) > 0

  const updateSettingInstructions = useCallback(
    (data: DigitalTwinSettingCreationRequest & { delete?: boolean; id?: number }) => {
      setEventPost((previousEventPost): EventPost => {
        const updatingSetting = data.id !== undefined && data.id !== null && !data.delete

        const linkedObjectOfNewSetting = previousEventPost.setting_instructions?.linked_objects?.find(
          (linkedObject) =>
            linkedObject.level === data.level &&
            linkedObject.name === data.name &&
            linkedObject.attribute === data.attribute
        )

        if (!linkedObjectOfNewSetting) return previousEventPost
        const settingsAndTimeSeriesSettingsInstructionsForLinkedObject = [
          ...linkedObjectOfNewSetting.setting_instructions,
          ...linkedObjectOfNewSetting.time_series_setting_instructions,
        ]

        if (data.isTimeSeries) {
          //Delete, update and create for time series settings
          const timeSeriesSettingData: TimeSeriesSettingCreationRequest[] = splitTimeSeriesSettingOnNullValues(
            data as TimeSeriesSettingCreationRequest
          )
          let allTimeSeriesSettingInstructionsForLinkedObject = [
            ...linkedObjectOfNewSetting.time_series_setting_instructions,
          ]

          if (data.delete) {
            const indexOfDeletedSetting = allTimeSeriesSettingInstructionsForLinkedObject.findIndex(
              (setting) => setting.id === data.id
            )
            if (indexOfDeletedSetting !== -1) {
              allTimeSeriesSettingInstructionsForLinkedObject.splice(indexOfDeletedSetting, 1)
            }
          } else {
            const newSettings: TimeSeriesSettingInstruction[] = []
            timeSeriesSettingData.forEach((timeSeriesSetting) => {
              const times = Datetime.getHoursBetween(timeSeriesSetting.start_time, timeSeriesSetting.end_time)
              const value = times
                .map((time) => (time in timeSeriesSetting.value ? timeSeriesSetting.value[time].value : null))
                .filter((v): v is number => v !== null)

              const newId = generateUniqueId(newSettings.length, [
                ...settingsAndTimeSeriesSettingsInstructionsForLinkedObject,
                ...newSettings,
              ])
              const newTimeSeriesSettingInstruction = {
                level: timeSeriesSetting.level || '',
                name: timeSeriesSetting.name || '',
                attribute: timeSeriesSetting.attribute || '',
                priority: timeSeriesSetting.priority || 0,
                comment: timeSeriesSetting.comment || '',
                start_time: timeSeriesSetting.start_time || undefined,
                end_time: timeSeriesSetting.end_time || undefined,
                values: value as number[],
                creator_name: timeSeriesSetting.creator_name || '',
                updated_at: Datetime.getISONow(0, false),
                id: newId, //always new id if it has split
              }

              newSettings.push(newTimeSeriesSettingInstruction)
            })

            if (updatingSetting) {
              const indexOfUpdatedSetting = allTimeSeriesSettingInstructionsForLinkedObject.findIndex(
                (setting) => setting.id === data.id
              )
              if (indexOfUpdatedSetting !== -1) {
                allTimeSeriesSettingInstructionsForLinkedObject.splice(indexOfUpdatedSetting, 1)
              }
            }
            allTimeSeriesSettingInstructionsForLinkedObject = [
              ...allTimeSeriesSettingInstructionsForLinkedObject,
              ...newSettings.map((setting) => ({
                ...setting,
              })),
            ]
          }
          return {
            ...previousEventPost,
            setting_instructions: {
              linked_objects: (previousEventPost.setting_instructions?.linked_objects ?? []).map((linkedObject) => {
                if (
                  linkedObject.level === data.level &&
                  linkedObject.name === data.name &&
                  linkedObject.attribute === data.attribute
                ) {
                  return {
                    ...linkedObject,
                    time_series_setting_instructions: allTimeSeriesSettingInstructionsForLinkedObject,
                  }
                }
                return linkedObject
              }),
            },
          }
        } else {
          //Delete, update and create for "normal" settings
          const settingsData = setSettingRequestMinutes(data) as DigitalTwinSettingCreationRequest

          let allSettingInstructionsForLinkedObject = [...linkedObjectOfNewSetting.setting_instructions]

          if (data.delete) {
            allSettingInstructionsForLinkedObject = allSettingInstructionsForLinkedObject.filter(
              (setting) => setting.id !== data.id
            )
          } else {
            let newId = allSettingInstructionsForLinkedObject.length

            newId = generateUniqueId(newId, settingsAndTimeSeriesSettingsInstructionsForLinkedObject)
            const newInstruction = {
              level: settingsData.level || '',
              name: settingsData.name || '',
              attribute: settingsData.attribute || '',
              priority: settingsData.priority || 0,
              comment: settingsData.comment || '',
              start_time: settingsData.start_time || undefined,
              end_time: settingsData.end_time || undefined,
              value: settingsData.value as number,
              creator_name: settingsData.creator_name || '',
              updated_at: Datetime.getISONow(0, false),
              id: data.id ?? newId,
            }

            if (updatingSetting) {
              const indexOfUpdatedSetting = allSettingInstructionsForLinkedObject.findIndex(
                (setting) => setting.id === data.id
              )
              allSettingInstructionsForLinkedObject[indexOfUpdatedSetting] = newInstruction
            } else {
              allSettingInstructionsForLinkedObject.push(newInstruction)
            }
          }

          return {
            ...previousEventPost,
            setting_instructions: {
              linked_objects: (previousEventPost.setting_instructions?.linked_objects ?? []).map((linkedObject) => {
                if (
                  linkedObject.level === settingsData.level &&
                  linkedObject.name === settingsData.name &&
                  linkedObject.attribute === settingsData.attribute
                ) {
                  return {
                    ...linkedObject,
                    setting_instructions: allSettingInstructionsForLinkedObject,
                  }
                }
                return linkedObject
              }),
            },
          }
        }
      })
    },
    []
  )

  const resetEventPost = useCallback(() => {
    setEventPost(initialEventModalState)
    setLastSavedValues(initialEventModalState)
  }, [initialEventModalState, setLastSavedValues])

  const isEventInitialState = useCallback(
    (eventPost: EventPost): boolean => {
      return !hasInitialEventPostChanges(eventPost, initialEventModalState, eventConfig)
    },
    [initialEventModalState, eventConfig]
  )

  async function createOrUpdateEventPost(data: EventPost): Promise<EventPost> {
    if (!unsavedEvent) return updateEventPost(data)

    const created = await createEventPost(data)
    onEventCreated(created)

    return created
  }

  async function sendEvent(data: EventPost): Promise<void> {
    if (isSavingRef.current) {
      // prevent user to send app call while data is loading
      return
    }
    isSavingRef.current = true

    try {
      const updatedData = {
        ...data,
        digital_twin_id: activeDigitalTwinId,
        status: 'Sent' as EventStatusType,
      }
      const response = await createOrUpdateEventPost(updatedData)
      const updatedEventPostWithId = { ...updatedData, id: response.id }

      setEventPost(updatedEventPostWithId)
      setLastSavedValues(updatedEventPostWithId)
      alertStore.success(t(`Event submitted`), undefined, ALERT_KEYS.eventSubmitted)
      isSavingRef.current = false
    } catch (_e) {
      isSavingRef.current = false
      alertStore.error(t(`Failed to submit event`))
    }
  }
  async function draftEvent(data: EventPost): Promise<void> {
    if (isSavingRef.current) {
      // prevent user to send app call while data is loading
      return
    }
    isSavingRef.current = true
    try {
      const updatedData = {
        ...data,
        digital_twin_id: activeDigitalTwinId,
        status: 'Draft' as EventStatusType,
      }

      const response = await createOrUpdateEventPost(updatedData)
      const updatedEventPostWithId = { ...updatedData, id: response.id }

      setEventPost(updatedEventPostWithId)
      setLastSavedValues(updatedEventPostWithId)
      alertStore.success(t(`Event saved as draft`), undefined, ALERT_KEYS.eventSavedAsDraft)
      isSavingRef.current = false
    } catch (_e) {
      isSavingRef.current = false
      alertStore.error(t(`Failed to save event as draft`))
    }
  }

  const updateEvent = useCallback(
    async (data: EventPost) => {
      if (isSavingRef.current) return
      isSavingRef.current = true
      try {
        // A 204 parses to null, and the write still landed — the baseline has to advance either way
        // or the modal stays dirty forever and keeps withholding server revisions.
        const savedEventPost = (await updateEventPost(data)) ?? data
        setLastSavedValues(savedEventPost)
        setEventPost(savedEventPost)
        isSavingRef.current = false
      } catch (_e) {
        isSavingRef.current = false
        alertStore.error(t(`Failed to update event`))
      }
    },
    [t, updateEventPost, setLastSavedValues]
  )

  const activateEvent = useCallback(
    async (data: EventPost) => {
      if (isSavingRef.current) return
      isSavingRef.current = true
      try {
        const updatedData = {
          ...data,
          status: 'Active' as EventStatusType,
        }

        await updateEventPost(updatedData)
        setLastSavedValues(updatedData)
        setEventPost(updatedData)
        alertStore.success(t(`Event activated`), undefined, ALERT_KEYS.eventActivated)
        isSavingRef.current = false
      } catch (_e) {
        isSavingRef.current = false
        alertStore.error(t(`Failed to activate event`))
      }
    },
    [t, updateEventPost, setLastSavedValues]
  )

  const inactivateEvent = useCallback(
    async (data: EventPost) => {
      if (isSavingRef.current) return
      isSavingRef.current = true
      try {
        const updatedData = {
          ...data,
          status: 'Inactive' as EventStatusType,
        }

        await updateEventPost(updatedData)
        setLastSavedValues(updatedData)
        setEventPost(updatedData)
        alertStore.success(t(`Event inactivated`))
        isSavingRef.current = false
      } catch (_e) {
        isSavingRef.current = false
        alertStore.error(t(`Failed to inactivate event`))
      }
    },
    [t, updateEventPost, setLastSavedValues]
  )

  const deleteEvent = useCallback(
    async (id: number) => {
      if (isSavingRef.current) {
        // The confirmation dialog has already closed by this point, so dropping the delete without
        // saying so is indistinguishable to the user from one that succeeded.
        alertStore.error(t(`Another change is still saving. Try deleting again once it has finished.`))

        return
      }
      isSavingRef.current = true
      try {
        await deleteEventPost(id)
        resetEventPost()
        if (!isEventInitialState(eventPost)) {
          alertStore.success(t(`Event deleted`))
        }
        closeModal()
        isSavingRef.current = false
      } catch (_e) {
        isSavingRef.current = false
        alertStore.error(t(`Failed to delete event`))
      }
    },
    [deleteEventPost, eventPost, isEventInitialState, resetEventPost, t, closeModal]
  )

  const handleOnClose = useCallback((): void => {
    // Both confirmations offer a write — Save draft, or a throw-away that races the create — and
    // close the modal straight after it, so opened mid-write they would drop the save and still
    // close as if it had landed.
    if ((isWritingEvent || isSavingRef.current) && (unsavedEvent || isEditing)) {
      alertStore.error(t(`A change is still saving. Try closing again once it has finished.`))

      return
    }

    if (unsavedEvent) {
      dispatchUi({ type: 'set', key: 'confirmOnThrowAwayModalOpen', value: true })

      if (isEventInitialState(eventPost)) {
        closeModal()
      }
    } else if (isEditing && !unsavedEvent) {
      dispatchUi({ type: 'set', key: 'confirmOnCloseModalOpen', value: true })
    } else {
      resetEventPost()
      closeModal()
    }
  }, [isWritingEvent, unsavedEvent, isEditing, isEventInitialState, eventPost, closeModal, resetEventPost, t])

  const handleOnSaveEdit = useCallback((): void => {
    const updatedEventPost = {
      ...eventPost,
      status: eventPost.status,
    }
    setEventPost(updatedEventPost)
    updateEvent(updatedEventPost)
  }, [eventPost, updateEvent])

  function runWrite(write: EventPostModalWrite): void {
    if (write === 'save') handleOnSaveEdit()
    if (write === 'draft') draftEvent(eventPost)
    if (write === 'submit') sendEvent(eventPost)
    if (write === 'activate') activateEvent(eventPost)
    if (write === 'inactivate') inactivateEvent(eventPost)
  }

  function requestWrite(write: EventPostModalWrite): void {
    if (hasWithheldServerUpdate) {
      dispatchUi({ type: 'setPendingWrite', value: write })

      return
    }

    runWrite(write)
  }

  const addEventPropertyField = useCallback((): void => {
    const updatedEventProperties = [
      ...(linkedObjectFromInstructions || []),
      {
        level: '',
        name: '',
        attribute: EMPTY_LINKED_OBJECT_ATTRIBUTE,
        setting_instructions: [],
        time_series_setting_instructions: [],
      },
    ]
    setEventPost((prevEventPost) => ({
      ...prevEventPost,
      setting_instructions: {
        ...prevEventPost.setting_instructions,
        linked_objects: updatedEventProperties,
      },
    }))
  }, [linkedObjectFromInstructions])

  const removeEventPropertyField = useCallback(
    (index: number) => {
      const prevLinkedObjects = linkedObjectFromInstructions || []
      const updatedEventProperties = prevLinkedObjects.filter((_, i) => i !== index)
      const newEventPost = {
        ...eventPost,
        setting_instructions: {
          ...eventPost.setting_instructions,
          linked_objects: updatedEventProperties,
        },
      }

      setEventPost(newEventPost)
    },
    [linkedObjectFromInstructions, eventPost]
  )

  const toggleSettingOverview = useCallback((index: number) => {
    setViewState((prevState) => ({
      ...prevState,
      openSettingsOverview: {
        ...prevState.openSettingsOverview,
        [index]: prevState.openSettingsOverview[index] === undefined ? true : !prevState.openSettingsOverview[index],
      },
    }))
  }, [])

  const inputFieldEditorContent = useInputFieldEditorContent(eventPost, eventConfig)
  useEffect(() => {
    if (lastSeededSavedValues.current === lastSavedValues) return

    lastSeededSavedValues.current = lastSavedValues

    const editor = quillRef.current?.getEditor?.()
    if (!editor?.getContents) return

    const delta = new Delta(editor.getContents().ops)
    queueMicrotask(() => {
      setInputFieldDeltaState({ saved: delta, current: delta })
    })
  }, [lastSavedValues])

  const handleEditorChange = useCallback(
    (content: string, source: string, editor: any) => {
      const editorContents = new Delta(editor.getContents().ops)

      if (source !== 'user') {
        if (lastSeededSavedValues.current !== lastSavedValues) {
          lastSeededSavedValues.current = lastSavedValues
          setInputFieldDeltaState({ saved: editorContents, current: editorContents })
        }

        return
      }

      setInputFieldDeltaState((previous) => ({ ...previous, current: editorContents }))

      const currentEventType = eventPost.operational_event_type
      if (!currentEventType) return

      const currentValue = eventPost?.input_field?.[currentEventType] ?? ''
      if (isEquivalentEditorContent(content, currentValue, inputFieldEditorContent)) {
        return
      }

      if (hasOversizedEmbeddedImage(editorContents)) {
        setViewState((prevState) => ({
          ...prevState,
          editorErrorMessage: `The image size exceeds the maximum limit of ${MAX_IMAGE_SIZE_MB}MB.`,
        }))
      } else {
        setViewState((prevState) => ({ ...prevState, editorErrorMessage: '' }))
      }

      const newEventPost = {
        ...eventPost,
        input_field: {
          ...eventPost.input_field,
          [currentEventType as string]: content,
        },
      }
      setEventPost(newEventPost)
    },
    [eventPost, inputFieldEditorContent, lastSavedValues]
  )
  const disabledSameLinkedObjects = hasDuplicateLinkedObjects(linkedObjectFromInstructions || [])

  const disabledSubmit = useMemo(() => {
    return (
      eventPost.name === undefined ||
      eventPost.name === '' ||
      eventPost.operational_event_type === null ||
      eventPost.operational_event_type === undefined ||
      eventPost.category_id === null ||
      eventPost.category_id === undefined ||
      eventPost.schedulability === null ||
      eventPost.schedulability === undefined ||
      viewState.hasInvalidDates ||
      viewState.hasInvalidDateLimit ||
      linkedObjectFromInstructions?.some((linkedObject) => linkedObject.level === '' || linkedObject.name === '') ||
      disabledSameLinkedObjects ||
      !!viewState.editorErrorMessage
    )
  }, [disabledSameLinkedObjects, eventPost, viewState, linkedObjectFromInstructions])

  const disabledSaveDraft = useMemo(
    () =>
      viewState.hasInvalidDateLimit ||
      viewState.hasInvalidDates ||
      !!viewState.editorErrorMessage ||
      (linkedObjectFromInstructions?.some((linkedObject) => linkedObject.level === '' || linkedObject.name === '') ??
        false) ||
      disabledSameLinkedObjects,
    [viewState, disabledSameLinkedObjects, linkedObjectFromInstructions]
  )

  const createdAndUpdatedInfo = createdAndUpdatedInfoEventPost(eventPost, t)

  const hasAccessToUMMCheckbox = hasAccess({ submodule: 'operational_event_umm', module: 'events' })

  function permittedTo(action: PermittedActionsEventPost): boolean {
    return eventPost.permitted_actions ? eventPost.permitted_actions.includes(action) : false
  }

  const disableQuillEditor = !permittedTo('update') || !eventPost.operational_event_type

  return (
    <>
      <Dialog
        open={true}
        fullWidth
        maxWidth="lg"
        onClose={(_, reason) => {
          if (reason === 'escapeKeyDown') {
            handleOnClose()
          }
        }}
        slotProps={{ paper: { 'data-testid': 'event-post-dialog' } }}
      >
        <CopyDialogTitle
          title={eventPost.name ? eventPost.name : t('New event')}
          handleClose={() => handleOnClose()}
          onTitleClick={
            eventPost.id
              ? () => {
                  const url = `${window.location.origin}/events/${eventPost.id}`
                  navigator.clipboard.writeText(url)
                  alertStore.success(t('URL copied to clipboard'))
                }
              : undefined
          }
          titleTooltip={eventPost.id ? t('Click to copy link') : undefined}
          infoText={
            (!permittedTo('update') || (hasChangeRequest && CHANGE_REQUEST_PERMISSION)) && (
              <InfoBanner
                text={
                  hasChangeRequest && CHANGE_REQUEST_PERMISSION
                    ? t('This event has a change request')
                    : !permittedTo('update')
                      ? t('Note that you do not have permission to change or update this event')
                      : ''
                }
                style={'warning'}
                variant={hasChangeRequest && CHANGE_REQUEST_PERMISSION ? 'textWithBackground' : 'text'}
              />
            )
          }
        />
        <DialogContent>
          <div className={styles.EventPostModal_FormRow}>
            <TextField
              required
              fullWidth
              margin="dense"
              label={t('Name event')}
              disabled={!permittedTo('update')}
              value={eventPost.name ?? ''}
              slotProps={{ htmlInput: { 'data-testid': 'event-name-input' } }} // e2e: event name input — do not remove
              onChange={(e) => {
                const newEventPost = {
                  ...eventPost,
                  name: e.target.value as string,
                }
                setEventPost(newEventPost)
              }}
            />
            <Dropdown
              fullWidth
              margin="dense"
              label={t('Event type') + ' *'}
              data-testid="event-type-select" // e2e: event type select — do not remove
              disabled={!permittedTo('update')}
              items={EVENT_TYPES.map((type) => ({ value: type.value, label: t(type.label) }))}
              value={eventPost.operational_event_type ?? ''}
              handleChange={(e) => {
                const newEventPost = {
                  ...eventPost,
                  operational_event_type: e.target.value as EventPost['operational_event_type'],
                }
                setEventPost(newEventPost)
              }}
            />
            <SearchableDropdown
              fullWidth
              margin="dense"
              label={t('Cause category') + ' *'}
              data-testid="cause-category-select" // e2e: cause category select — do not remove
              disabled={!permittedTo('update')}
              items={
                Array.isArray(eventCategories)
                  ? eventCategories.map((type) => ({
                      value: type.id ?? '',
                      label: type.name ?? '',
                      subHeader: type.category_group?.name ?? '',
                    }))
                  : []
              }
              value={eventPost.category_id ?? ''}
              handleChange={(value) => {
                const newEventPost = {
                  ...eventPost,
                  category_id: value as number,
                }
                setEventPost(newEventPost)
              }}
            />
          </div>
          <div className={styles.EventPostModal_FormRow}>
            <div className={styles.EventPostModal_DatePickerPeriod}>
              <Tooltip
                title={!permittedTo('update') ? t('You do not have permission to update these fields') : ''}
                arrow
              >
                <div>
                  <DatePickerPeriod
                    isDisabled={!permittedTo('update')}
                    startDate={eventPost.start_time ? eventPost.start_time : Datetime.getISONow()}
                    endDate={eventPost.end_time ? eventPost.end_time : Datetime.getEndOfDay(Datetime.getISONow())}
                    onSelect={(_startTime, _endTime) => {
                      const startTime = _startTime
                      const endTime = _endTime
                      const newStartTime = startTime
                        ? Datetime.toISOString(startTime)
                        : Datetime.getStartOfDay(Datetime.getISONow())
                      const newEndTime = endTime
                        ? Datetime.toISOString(endTime)
                        : Datetime.getEndOfDay(Datetime.getISONow())
                      const newEventPost = {
                        ...eventPost,
                        start_time: newStartTime,
                        end_time: newEndTime,
                      }
                      setEventPost(newEventPost)
                    }}
                    onError={(hasInvalidDates) =>
                      setViewState((prevState) =>
                        prevState.hasInvalidDates === hasInvalidDates ? prevState : { ...prevState, hasInvalidDates }
                      )
                    }
                    minDate={
                      eventPost.earliest_start_time
                        ? Datetime.toISOString(eventPost.earliest_start_time)
                        : Datetime.toISOString('2000-01-01T00:00:00+00:00')
                    }
                    maxDate={
                      eventPost.latest_end_time
                        ? Datetime.toISOString(eventPost.latest_end_time)
                        : Datetime.toISOString('2100-01-01T00:00:00+00:00')
                    }
                  />
                </div>
              </Tooltip>
            </div>
            <div className={styles.EventPostModal_Schedulability}>
              <Dropdown
                fullWidth
                margin="dense"
                label={t('Schedulability') + ' *'}
                data-testid="schedulability-select" // e2e: schedulability select — do not remove
                disabled={!permittedTo('update')}
                items={SCHEDULABILITIES.map((type) => ({ value: type.value, label: t(type.label) }))}
                value={eventPost.schedulability ?? ''}
                handleChange={(e) => {
                  const newEventPost = getEventPostForSchedulabilityChange(
                    eventPost,
                    e.target.value as EventPost['schedulability']
                  )
                  setEventPost(newEventPost)
                }}
              />
            </div>
          </div>
          {(eventPost.schedulability === 'MoveableWithinLimits' || eventPost.schedulability === 'AlwaysMoveable') && (
            <>
              <div className={styles.EventPostModal_FormRow}>
                {eventPost.schedulability === 'MoveableWithinLimits' && (
                  <Tooltip
                    title={!permittedTo('update') ? t('You do not have permission to update these fields') : ''}
                    arrow
                  >
                    <div className={styles.EventPostModal_DatePickerPeriod_FormRow}>
                      <DatePickerPeriod
                        isDisabled={!permittedTo('update')}
                        startDate={
                          eventPost.earliest_start_time
                            ? eventPost.earliest_start_time
                            : Datetime.toISOString(Datetime.subtractHours(eventPost.start_time, 24))
                        }
                        endDate={
                          eventPost.latest_end_time
                            ? eventPost.latest_end_time
                            : Datetime.toISOString(Datetime.addHours(eventPost.end_time, 24))
                        }
                        onSelect={(startTime, endTime) => {
                          const newStartTime = startTime
                            ? Datetime.toISOString(startTime)
                            : Datetime.toISOString(Datetime.subtractHours(eventPost.start_time, 24))
                          const newEndTime = endTime
                            ? Datetime.toISOString(endTime)
                            : Datetime.toISOString(Datetime.addHours(eventPost.end_time, 24))
                          const newEventPost = {
                            ...eventPost,
                            earliest_start_time: newStartTime,
                            latest_end_time: newEndTime,
                          }
                          setEventPost(newEventPost)
                        }}
                        onError={(hasInvalidDateLimit) =>
                          setViewState((prevState) =>
                            prevState.hasInvalidDateLimit === hasInvalidDateLimit
                              ? prevState
                              : { ...prevState, hasInvalidDateLimit }
                          )
                        }
                        startLabel={t('Starts earliest')}
                        endLabel={t('Ends latest')}
                      />
                    </div>
                  </Tooltip>
                )}
                <div className={styles.EventPostModal_Consequence}>
                  <TextField
                    fullWidth
                    margin="dense"
                    label={
                      t('Consequence of time adjustment') +
                      (schedulabilityEffectLength > 0
                        ? ` (${schedulabilityEffectLength}/${MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH})`
                        : '')
                    }
                    disabled={!permittedTo('update')}
                    value={eventPost.schedulability_effect}
                    onChange={(e) => {
                      const newEventPost = {
                        ...eventPost,
                        schedulability_effect: e.target.value,
                      }
                      setEventPost(newEventPost)
                    }}
                    slotProps={{
                      htmlInput: { maxLength: MAX_SCHEDULABILITY_EFFECT_INPUT_LENGTH },
                    }}
                  />
                </div>
              </div>
            </>
          )}
          {isSettingsOutsideTimePeriod && (
            <div className={`${styles.EventPostModal_FormRow}`} style={{ marginBottom: '10px' }}>
              <InfoBanner
                text={t(`There are settings in this event outside the event time period`)}
                style={'warning'}
              />
            </div>
          )}
          <Tooltip
            title={!eventPost.operational_event_type ? t('Choose an event type to be able to edit') : ''}
            arrow
            placement="top"
          >
            <div className={styles.EventPostModal_FourthRow}>
              <ReactQuill
                ref={quillRef}
                modules={reactQuillModules}
                value={inputFieldEditorContent}
                readOnly={disableQuillEditor}
                className={classNames(styles.EventPostModal_QuillEditor, {
                  [styles.EventPostModal_QuillEditor_Disabled]: disableQuillEditor,
                })}
                //delta and source, even though unused in the function, are required by Quills internal state management
                onChange={(content, _delta, source, editor) => handleEditorChange(content, source, editor)}
              />
            </div>
          </Tooltip>
          {viewState.editorErrorMessage && (
            <div className={styles.EventPostModal_EditorErrorMessage}>{t(viewState.editorErrorMessage)}</div>
          )}
          <div className={styles.EventPostModal_FormRow}>
            {hasAccessToUMMCheckbox && (
              <FormGroup>
                <FormControlLabel
                  label={t('Requires UMM')}
                  disabled={!permittedTo('update')}
                  className={
                    eventPost.require_umm
                      ? styles.EventPostModal_CheckboxYellowBackground
                      : styles.EventPostModal_Checkbox
                  }
                  control={
                    <MuiCheckbox
                      checked={eventPost.require_umm}
                      onChange={() => {
                        const newEventPost = {
                          ...eventPost,
                          require_umm: !eventPost.require_umm,
                        }
                        setEventPost(newEventPost)
                      }}
                    />
                  }
                />
              </FormGroup>
            )}

            <FormGroup>
              <FormControlLabel
                label={t('Cancelable after start')}
                disabled={!permittedTo('update')}
                className={styles.EventPostModal_Checkbox}
                control={
                  <MuiCheckbox
                    checked={!!eventPost.cancelable_after_start}
                    onChange={() => {
                      const newEventPost = {
                        ...eventPost,
                        cancelable_after_start: !eventPost.cancelable_after_start,
                      }
                      setEventPost(newEventPost)
                    }}
                  />
                }
              />
            </FormGroup>
          </div>
          <EventPostModalAffectedComponentsSection
            isSettingsOutsideTimePeriod={isSettingsOutsideTimePeriod}
            settingInstructions={settingInstructions}
            eventPost={eventPost}
            linkedObjectFromInstructions={linkedObjectFromInstructions}
            disabledSameLinkedObjects={disabledSameLinkedObjects}
            permittedTo={permittedTo}
            dispatchUi={dispatchUi}
            toggleSettingOverview={toggleSettingOverview}
            openSettingsOverview={viewState.openSettingsOverview}
            updateSettingInstructions={updateSettingInstructions}
            setEventPost={setEventPost}
            addEventPropertyField={addEventPropertyField}
          />

          <Divider className={styles.EventPostModal_Divider} />
          <div className={styles.EventPostModal_EventInformation}>
            <p style={{ fontStyle: 'italic' }}>{createdAndUpdatedInfo}</p>
            <EventStatus status={currentStatus} />
          </div>

          <EventPostModalActionButtons
            statusDraft={statusDraft}
            statusSubmitted={statusSubmitted}
            statusActive={statusActive}
            statusInactive={statusInactive}
            isEditing={isEditing}
            isFetching={isFetching}
            isWritingEvent={isWritingEvent}
            disabledSaveDraft={disabledSaveDraft}
            disabledSubmit={disabledSubmit}
            hasChangeRequest={hasChangeRequest}
            CHANGE_REQUEST_PERMISSION={CHANGE_REQUEST_PERMISSION}
            eventPost={eventPost}
            permittedTo={permittedTo}
            requestWrite={requestWrite}
            dispatchUi={dispatchUi}
          />
        </DialogContent>
      </Dialog>
      <EventPostModalDialogs
        uiState={uiState}
        unsavedEvent={unsavedEvent}
        eventPost={eventPost}
        statusDraft={statusDraft}
        disabledSaveDraft={disabledSaveDraft}
        closeModal={closeModal}
        deleteEvent={deleteEvent}
        resetEventPost={resetEventPost}
        draftEvent={draftEvent}
        removeEventPropertyField={removeEventPropertyField}
        requestWrite={requestWrite}
        runWrite={runWrite}
        hasWithheldServerUpdate={hasWithheldServerUpdate}
        dispatchUi={dispatchUi}
        CHANGE_REQUEST_PERMISSION={CHANGE_REQUEST_PERMISSION}
        hasChangeRequest={hasChangeRequest}
        setEventPost={setEventPost}
      />
    </>
  )
}
